import BuildingIcon from "@assets/svgs/building.svg?react";
import CalendarIcon from "@assets/svgs/calendar.svg?react";
import LocationIcon from "@assets/svgs/location.svg?react";
import WebsiteIcon from "@assets/svgs/website.svg?react";
import { Typography, useFetchFile, UserTypeEnum } from "@cpj-ai/panel-npm-package";
import { AuthAccountContactTypeChoices, GetEmployementOverviewQuery } from "@graphql/generated";
import { Avatar, Card } from "@heroui/react";
import dayjs from "dayjs";
import { useMemo } from "react";

interface OrganizationInfoCardProps {
  organization: NonNullable<NonNullable<GetEmployementOverviewQuery["me"]>["currentEmployement"]>["organization"];
}

const OrganizationInfoCard = ({ organization }: OrganizationInfoCardProps) => {
  const website = useMemo(() => {
    const website = organization?.contacts?.find((item) => item?.type === AuthAccountContactTypeChoices.WEBSITE)?.value;
    if (website) return new URL(website);
    return null;
  }, [organization?.contacts]);

  const { data: companyLogo } = useFetchFile({ urlPath: organization?.logo?.file, userType: UserTypeEnum.USER });

  return (
    <Card>
      <div className="p-3 md:p-5">
        <div className="flex items-center gap-2 md:gap-4">
          <Avatar
            radius="lg"
            src={companyLogo}
            isDisabled={!companyLogo}
            classNames={{ img: "opacity-100" }}
            className="w-[50px] min-w-[50px] h-[50px] md:w-[70px] md:min-w-[70px] md:h-[70px]"
          />
          <div className="flex flex-col gap-1 md:gap-2">
            <div className="flex flex-col gap-0.5 md:gap-1">
              <Typography className="text-base md:text-lg font-semibold">{organization?.name}</Typography>
              {organization?.industry && (
                <Typography className="text-xs md:text-sm text-[#A1A1AA]">{organization?.industry.title}</Typography>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:gap-6 lg:gap-12 xl:gap-24 mt-4">
          <div className="flex items-center gap-2 mb-2 md:mb-0">
            <WebsiteIcon className="w-5 h-5 md:w-6 md:h-6" />
            <Typography className="text-xs md:text-sm text-zinc-600">Website: </Typography>
            <a
              href={website?.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs md:text-sm text-blue-500 hover:underline"
            >
              {website?.hostname}
            </a>
          </div>

          <div className="flex items-center gap-2 mb-2 md:mb-0">
            <LocationIcon className="w-5 h-5 md:w-6 md:h-6" />
            <Typography className="text-xs md:text-sm text-zinc-600">Location:</Typography>
            <Typography className="text-xs md:text-sm">{`${organization?.city?.name || "-"} ${organization?.city?.country?.name ? "," + organization?.city?.country?.name : ""}`}</Typography>
          </div>

          <div className="flex items-center gap-2 mb-2 md:mb-0">
            <BuildingIcon className="w-5 h-5 md:w-6 md:h-6" />
            <Typography className="text-xs md:text-sm text-zinc-600">Company size: </Typography>
            <Typography className="text-xs md:text-sm">
              {organization?.size?.split("_").slice(1).join(" - ")}
            </Typography>
          </div>

          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 md:w-6 md:h-6" />
            <Typography className="text-xs md:text-sm text-zinc-600">Founded: </Typography>
            <Typography className="text-xs md:text-sm">{dayjs(organization?.establishedAt).format("YYYY")}</Typography>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default OrganizationInfoCard;
